/**
 * AnalyticsProvider — Single source of truth for GA4 tracking.
 *
 * Fixes discovered in the audit:
 *   1. Two competing GA4 loaders (hardcoded in index.html + dynamic in services/analytics.js).
 *      The dynamic one was never initialised — services/analytics.js.init() was never called.
 *   2. SPA route changes were NOT firing page_view events. React Router navigations were
 *      invisible to GA4 — only the initial hard load counted. This is why GA4 reported
 *      285 views for 569 users while Emergent (server-side) counted 14.3K requests.
 *   3. No Google Consent Mode v2 → GDPR-compliant users who declined cookies had ZERO
 *      tracking, when Consent Mode allows anonymized/aggregated pings without cookies.
 *
 * How this fixes it:
 *   • On mount: sets Consent Mode v2 defaults (denied for storage, granted for essentials),
 *     then loads gtag once via the hardcoded ID in index.html (already loaded).
 *   • On every react-router `useLocation` change: fires `gtag('event', 'page_view', ...)`
 *     with the new page_path + page_title. This is what makes SPA nav visible in GA4.
 *   • On `cookieConsentUpdated` DOM event: upgrades gtag consent to 'granted' so full
 *     tracking (with cookies + user_id) kicks in AFTER explicit consent.
 *
 * The GA4 measurement ID is set in ONE place: `public/index.html`. Do not add gtag
 * scripts anywhere else. Admin can override via /admin/settings but the env-hardcoded
 * ID is the canonical production value.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Must match the ID in public/index.html. Kept here so trackers can read it.
export const GA4_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-0X6Y4LQ7XY';

// ---- Consent Mode v2 helpers -----------------------------------------------

function setConsentDefaults() {
  if (!window.gtag) return;
  // Deny by default (GDPR safe). Enhanced Measurement events still fire in
  // cookieless mode when consent is denied — GA4 sends "ping" beacons that are
  // aggregated but not attributed to a persistent user_id.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function updateConsent(preferences) {
  if (!window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: preferences?.analytics ? 'granted' : 'denied',
    ad_storage: preferences?.marketing ? 'granted' : 'denied',
    ad_user_data: preferences?.marketing ? 'granted' : 'denied',
    ad_personalization: preferences?.marketing ? 'granted' : 'denied',
  });
}

// ---- Component -------------------------------------------------------------

const AnalyticsProvider = ({ children }) => {
  const location = useLocation();
  const initialized = useRef(false);

  // One-time setup: consent defaults + saved preferences + event listener
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    setConsentDefaults();

    // Restore any previously-saved consent
    try {
      const saved = JSON.parse(localStorage.getItem('cookie_consent') || 'null');
      if (saved?.preferences) updateConsent(saved.preferences);
    } catch { /* ignore */ }

    const onConsent = (e) => updateConsent(e.detail);
    window.addEventListener('cookieConsentUpdated', onConsent);
    return () => window.removeEventListener('cookieConsentUpdated', onConsent);
  }, []);

  // Fire a page_view on every SPA route change (the critical fix)
  useEffect(() => {
    if (!window.gtag) return;
    const path = location.pathname + location.search;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA4_MEASUREMENT_ID,
    });
  }, [location.pathname, location.search]);

  return children;
};

export default AnalyticsProvider;
